/**
 * Sneakers Enzo - Product Detail Page.
 * Vanilla JS, no dependencies. Coordinates the gallery, size picker, colour
 * swatches, quantity, buy buttons, sticky bar, size guide modal, PDP details
 * detail panels (tabs/accordion) and the lazy-loaded related products rail.
 *
 * Cart integration: adds to cart via the standard Shopify Ajax Cart API
 * (`/cart/add.js`). When the theme's global `@shopify/events` import map and
 * `#cart-drawer` (Horizon's <theme-drawer>) are present on the page, it also
 * dispatches a real `CartLinesUpdateEvent` so the theme's own cart icon /
 * cart drawer refresh themselves exactly as they do for native Horizon add-
 * to-cart forms, and opens the drawer when `cart_type` is `drawer`. Both are
 * optional, defensively guarded, and the PDP fully works without them
 * (falls back to an inline "added" state).
 */
(function () {
  'use strict';

  if (window.__senzoProductLoaded) return;
  window.__senzoProductLoaded = true;

  /* ---------------------------------------------------------------------
   * Utilities
   * ------------------------------------------------------------------- */

  /*
    Formats cents using the shop's own money format, read from
    data-money-format on the PDP root. Previously this hardcoded a euro sign,
    so a store on any other currency rendered the Liquid-side price correctly
    on load and then had it overwritten with the wrong symbol on the first
    variant change.
  */
  function groupThousands(intPart, sep) {
    return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  }

  function moneyFormat(cents, format) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';

    var fmt = format || moneyFormat.shopFormat || '€{{amount_with_comma_separator}}';
    var amount = cents / 100;

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, token) {
      var fixed, parts;
      switch (token) {
        case 'amount_no_decimals':
          return groupThousands(String(Math.round(amount)), ',');
        case 'amount_with_comma_separator':
          parts = amount.toFixed(2).split('.');
          return groupThousands(parts[0], '.') + ',' + parts[1];
        case 'amount_no_decimals_with_comma_separator':
          return groupThousands(String(Math.round(amount)), '.');
        case 'amount_with_apostrophe_separator':
          parts = amount.toFixed(2).split('.');
          return groupThousands(parts[0], "'") + '.' + parts[1];
        case 'amount_no_decimals_with_space_separator':
          return groupThousands(String(Math.round(amount)), ' ');
        case 'amount_with_space_separator':
          parts = amount.toFixed(2).split('.');
          return groupThousands(parts[0], ' ') + ',' + parts[1];
        default: // 'amount'
          fixed = amount.toFixed(2).split('.');
          return groupThousands(fixed[0], ',') + '.' + fixed[1];
      }
    });
  }

  // Picked up once from the PDP root so every call site stays a one-arg call.
  (function captureShopMoneyFormat() {
    var root = document.querySelector('[data-senzo-product][data-money-format]');
    if (root) moneyFormat.shopFormat = root.getAttribute('data-money-format');
  })();

  /*
    Substitutes the quantity token in a merchant-authored label. The token is
    a literal like [aantal] rather than {{ aantal }}: a {{ }} pair inside a
    schema `default` is parsed by Shopify as a dynamic source and the default
    never reaches the storefront. Split/join rather than a regex so a token
    containing regex metacharacters still works.
  */
  function fillQuantity(template, qty, token) {
    if (!template) return '';
    return template.split(token || '[aantal]').join(String(qty));
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  /* ---------------------------------------------------------------------
   * Main product controller: variants, size picker, swatches, buy buttons
   * ------------------------------------------------------------------- */

  function SenzoProduct(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId;
    this.cartType = root.dataset.cartType;
    this.lowStock = parseInt(root.dataset.lowStock, 10) || 3;

    var variantsScript = root.querySelector('[data-senzo-variants]');
    try {
      this.variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];
    } catch (e) {
      this.variants = [];
    }

    /*
      Real stock counts, keyed by variant id, rendered by the section.

      `{{ product.variants | json }}` above has no `inventory_quantity` key at
      all - the storefront variant object simply does not carry it. This code
      used to read it anyway, get `undefined`, coerce it to 0, and so never
      reach a single low-stock branch. Only ids that Shopify actually tracks
      are in the map; a missing id means the quantity is unknown, and unknown
      must never be presented as scarce.
    */
    this.inventory = {};
    try {
      var inventoryRaw = root.dataset.senzoInventory;
      if (inventoryRaw) this.inventory = JSON.parse(inventoryRaw) || {};
    } catch (e) {
      this.inventory = {};
    }

    this.variantInput = root.querySelector('[data-senzo-variant-input]');
    this.form = root.querySelector('.senzo-pdp__form');
    this.sizeGroup = root.querySelector('[data-senzo-size-group]');
    this.sizePicker = root.querySelector('[data-senzo-size-picker]');
    this.swatchGroup = root.querySelector('[data-senzo-swatch-group]');
    this.colourPicker = root.querySelector('[data-senzo-colour-picker]');
    this.addButton = root.querySelector('[data-senzo-add-to-cart]');
    this.dynamicCheckout = root.querySelector('[data-senzo-dynamic-checkout]');
    this.gallery = root.querySelector('[data-senzo-gallery]');
    this.stickyBar = root.querySelector('[data-senzo-sticky-bar]');

    this.selectedOptions = {};
    this.currentVariant = null;

    this.init();
  }

  SenzoProduct.prototype.init = function () {
    var startId = this.variantInput ? this.variantInput.value : null;
    var startVariant = this.findVariantById(startId) || this.variants[0] || null;

    if (startVariant) {
      var self = this;
      startVariant.options.forEach(function (value, i) {
        self.selectedOptions[i + 1] = value;
      });
    }

    this.bindSizePicker();
    this.bindSwatches();
    this.bindQuantity();
    this.bindAddToCart();
    this.bindGallery();
    this.bindLightbox();
    this.bindSizeGuide();
    this.bindShare();
    this.bindNotify();
    this.bindStickyBar();
    this.bindExcerptScroll();

    if (startVariant) this.applyVariant(startVariant, { skipHistory: true, skipGallery: true });
  };

  /*
    The tracked quantity of a variant, or null when it is unknown (untracked
    inventory, or a variant that is not in the map). Callers must branch on
    `null` explicitly and say nothing - treating it as 0 was the original bug.
  */
  SenzoProduct.prototype.quantityFor = function (variant) {
    if (!variant) return null;
    var qty = this.inventory[String(variant.id)];
    return typeof qty === 'number' ? qty : null;
  };

  /*
    Returns the remaining count when this variant is genuinely nearly gone -
    buyable, tracked, and at or under the merchant's threshold - and null in
    every other case, including "we do not know". The count is returned rather
    than a boolean because the copy contains the number.
  */
  SenzoProduct.prototype.lowStockCount = function (variant) {
    if (!variant || !variant.available) return null;
    var qty = this.quantityFor(variant);
    if (qty === null || qty <= 0 || qty > this.lowStock) return null;
    return qty;
  };

  SenzoProduct.prototype.findVariantById = function (id) {
    if (!id) return null;
    id = String(id);
    for (var i = 0; i < this.variants.length; i++) {
      if (String(this.variants[i].id) === id) return this.variants[i];
    }
    return null;
  };

  SenzoProduct.prototype.findVariantByOptions = function (options) {
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i];
      var match = true;
      for (var pos in options) {
        if (v.options[pos - 1] !== options[pos]) {
          match = false;
          break;
        }
      }
      if (match) return v;
    }
    return null;
  };

  /* -- Size picker -------------------------------------------------------- */

  SenzoProduct.prototype.bindSizePicker = function () {
    if (!this.sizeGroup) return;
    var self = this;
    var chips = Array.prototype.slice.call(this.sizeGroup.querySelectorAll('[data-senzo-size]'));

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        self.selectSizeChip(chip);
      });
    });

    this.sizeGroup.addEventListener('keydown', function (event) {
      var keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
      if (keys.indexOf(event.key) === -1) return;
      event.preventDefault();

      var enabledChips = chips;
      var currentIndex = enabledChips.indexOf(document.activeElement);
      if (currentIndex === -1) currentIndex = 0;
      var nextIndex = currentIndex;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % enabledChips.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + enabledChips.length) % enabledChips.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = enabledChips.length - 1;
      }

      var next = enabledChips[nextIndex];
      if (next) {
        self.selectSizeChip(next);
        next.focus();
      }
    });

    this.sizeChips = chips;
  };

  SenzoProduct.prototype.selectSizeChip = function (chip) {
    var self = this;
    if (!this.sizeGroup) return;
    var chips = this.sizeChips || [];

    chips.forEach(function (c) {
      c.setAttribute('aria-checked', c === chip ? 'true' : 'false');
      c.tabIndex = c === chip ? 0 : -1;
    });

    var position = parseInt(this.sizePicker.dataset.optionPosition, 10);
    this.selectedOptions[position] = chip.dataset.value;

    var errorEl = this.root.querySelector('[data-senzo-size-error]');
    if (errorEl) errorEl.hidden = true;

    var isOut = chip.dataset.available === 'false' || chip.classList.contains('senzo-size--out');
    this.toggleNotify(isOut, chip.dataset.value);

    var variant = this.findVariantByOptions(this.selectedOptions);
    if (variant) this.applyVariant(variant, {});
  };

  /* -- Colour swatches ------------------------------------------------------ */

  SenzoProduct.prototype.bindSwatches = function () {
    if (!this.swatchGroup) return;
    var self = this;
    var swatches = Array.prototype.slice.call(this.swatchGroup.querySelectorAll('[data-senzo-swatch]'));

    swatches.forEach(function (swatch) {
      swatch.addEventListener('click', function () {
        self.selectSwatch(swatch, swatches);
      });
    });

    this.swatchGroup.addEventListener('keydown', function (event) {
      var keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'];
      if (keys.indexOf(event.key) === -1) return;
      event.preventDefault();
      var currentIndex = swatches.indexOf(document.activeElement);
      if (currentIndex === -1) currentIndex = 0;
      var nextIndex =
        event.key === 'ArrowRight' || event.key === 'ArrowDown'
          ? (currentIndex + 1) % swatches.length
          : (currentIndex - 1 + swatches.length) % swatches.length;
      var next = swatches[nextIndex];
      if (next) {
        self.selectSwatch(next, swatches);
        next.focus();
      }
    });
  };

  SenzoProduct.prototype.selectSwatch = function (swatch, swatches) {
    swatches.forEach(function (s) {
      s.setAttribute('aria-checked', s === swatch ? 'true' : 'false');
      s.tabIndex = s === swatch ? 0 : -1;
    });

    var position = parseInt(this.colourPicker.dataset.optionPosition, 10);
    this.selectedOptions[position] = swatch.dataset.value;

    var valueEl = this.root.querySelector('[data-senzo-colour-value]');
    if (valueEl) valueEl.textContent = swatch.dataset.value;

    this.refreshSizeAvailability();

    var variant = this.findVariantByOptions(this.selectedOptions);
    if (variant) {
      this.applyVariant(variant, {});
    } else {
      // No exact match for this colour + current size - keep colour, drop
      // the now-invalid size selection and let the shopper re-pick.
      var sizePosition = this.sizePicker && parseInt(this.sizePicker.dataset.optionPosition, 10);
      if (sizePosition) delete this.selectedOptions[sizePosition];
      var firstForColour = this.findVariantByOptions(this.selectedOptions);
      if (firstForColour) this.applyVariant(firstForColour, {});
    }
  };

  // Recomputes each size chip's available/low state for the currently
  // selected colour (or across all colours when none is selected yet).
  SenzoProduct.prototype.refreshSizeAvailability = function () {
    if (!this.sizeChips) return;
    var self = this;
    this.sizeChips.forEach(function (chip) {
      var ids = (chip.dataset.variantIds || '').split(',').filter(Boolean);
      var relevant = self.variants.filter(function (v) {
        return ids.indexOf(String(v.id)) !== -1;
      });

      // Narrow to variants matching every currently-selected option other
      // than size itself (i.e. the selected colour).
      var sizePosition = self.sizePicker ? parseInt(self.sizePicker.dataset.optionPosition, 10) : null;
      var narrowed = relevant.filter(function (v) {
        for (var pos in self.selectedOptions) {
          if (Number(pos) === sizePosition) continue;
          if (v.options[pos - 1] !== self.selectedOptions[pos]) return false;
        }
        return true;
      });
      var pool = narrowed.length ? narrowed : relevant;

      var available = pool.some(function (v) {
        return v.available;
      });

      /*
        Sum only the quantities we actually know. `qty` stays null while every
        variant in the pool is untracked, which keeps "unknown" distinct from
        "zero" - previously this read the non-existent inventory_quantity key,
        summed a pile of undefined into 0, and so wiped the low-stock class
        off every chip the moment a colour was picked.
      */
      var qty = null;
      pool.forEach(function (v) {
        var single = self.quantityFor(v);
        if (single === null) return;
        qty = (qty === null ? 0 : qty) + single;
      });
      var low = available && qty !== null && qty > 0 && qty <= self.lowStock;

      chip.dataset.available = available ? 'true' : 'false';
      chip.classList.toggle('senzo-size--out', !available);
      chip.classList.toggle('senzo-size--low', low);

      var note = chip.querySelector('[data-senzo-size-note]');
      if (note) {
        var noteData = self.sizePicker ? self.sizePicker.dataset : {};
        note.textContent = low ? ', ' + fillQuantity(noteData.lowStockNote, qty, noteData.qtyToken) : '';
      }
    });
  };

  /* -- Variant application: price, availability, sku, url, gallery -------- */

  SenzoProduct.prototype.applyVariant = function (variant, opts) {
    opts = opts || {};
    this.currentVariant = variant;

    if (this.variantInput) this.variantInput.value = variant.id;

    if (!opts.skipHistory && window.history && window.history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    // Price
    var priceEl = this.root.querySelector('[data-senzo-price-amount]');
    if (priceEl) priceEl.textContent = moneyFormat(variant.price);
    var compareEl = this.root.querySelector('[data-senzo-price-compare]');
    if (compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareEl.textContent = moneyFormat(variant.compare_at_price);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }
    var priceWrap = this.root.querySelector('[data-senzo-price]');
    if (priceWrap) priceWrap.classList.toggle('senzo-price--sale', !!compareEl && !compareEl.hidden);

    // SKU - hide the whole line when the variant has none, so the label never
    // renders on its own with an empty value after it.
    var skuEl = this.root.querySelector('[data-senzo-sku-value]');
    if (skuEl) {
      skuEl.textContent = variant.sku || '';
      var skuLine = this.root.querySelector('[data-senzo-sku]');
      if (skuLine) skuLine.hidden = !variant.sku;
    }

    /*
      Availability text. This runs on load too (init calls applyVariant), so
      it has to reproduce exactly what Liquid rendered - otherwise the correct
      server-rendered low-stock line is replaced by a wrong one milliseconds
      after the page appears, which is what used to happen.
    */
    var availEl = this.root.querySelector('[data-senzo-availability]');
    if (availEl) {
      var lowQty = this.lowStockCount(variant);
      var state = 'in';
      var label = availEl.dataset.inStockLabel;
      if (!variant.available) {
        state = 'out';
        label = availEl.dataset.outStockLabel;
      } else if (lowQty !== null) {
        state = 'low';
        label = fillQuantity(availEl.dataset.lowStockLabel, lowQty, availEl.dataset.qtyToken);
      }
      availEl.className = 'senzo-pdp__availability senzo-pdp__availability--' + state;
      var dot = availEl.querySelector('.senzo-pdp__availability-dot');
      if (dot) dot.className = 'senzo-pdp__availability-dot senzo-pdp__availability-dot--' + state;
      /*
        Only write when the wording actually changes. This element is an
        aria-live region and applyVariant also runs on page load, so blindly
        re-setting textContent would make a screen reader announce the stock
        line once for nothing every time the page opens.
      */
      var availText = availEl.querySelector('[data-senzo-availability-text]');
      if (availText && availText.textContent !== label) availText.textContent = label;
    }

    // Buy button state
    if (this.addButton) {
      var labelEl = this.addButton.querySelector('[data-senzo-add-label]');
      this.addButton.classList.remove('is-added');
      if (variant.available) {
        this.addButton.disabled = false;
        if (labelEl) labelEl.textContent = this.addButton.dataset.idleLabel;
      } else {
        this.addButton.disabled = true;
        if (labelEl) labelEl.textContent = this.addButton.dataset.soldOutLabel;
      }
    }

    // Accelerated checkout must follow the variant, not the page load. Hiding
    // is not enough on its own: a hidden-but-present button can still be
    // activated by assistive tech, so inert the controls inside it too.
    if (this.dynamicCheckout) {
      var buyable = !!variant.available;
      this.dynamicCheckout.hidden = !buyable;
      var kids = this.dynamicCheckout.querySelectorAll('button, [role="button"], shopify-accelerated-checkout, shopify-buy-it-now-button');
      for (var d = 0; d < kids.length; d++) {
        if (buyable) kids[d].removeAttribute('aria-hidden');
        else kids[d].setAttribute('aria-hidden', 'true');
      }
    }

    // Gallery
    if (!opts.skipGallery && variant.featured_media && this.gallery) {
      this.showGalleryMedia(variant.featured_media.id);
    }

    // Sticky bar mirror
    if (this.stickyBar) {
      var stickyPrice = this.stickyBar.querySelector('[data-senzo-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = moneyFormat(variant.price);
      /*
        `featured_image` in the variants JSON is an image OBJECT
        ({ src, width, height, alt, ... }), not a URL string. Calling
        .replace() on it threw a TypeError that aborted applyVariant right
        here - before the sticky size label and the sticky button's disabled
        state below were ever touched. It has never fired only because every
        variant in this catalogue happens to have a null image; one product
        with per-variant images would have broken every variant switch.
      */
      var stickyImg = this.stickyBar.querySelector('[data-senzo-sticky-image]');
      var stickySrc = variant.featured_image;
      if (stickySrc && typeof stickySrc === 'object') stickySrc = stickySrc.src;
      if (stickyImg) {
        if (typeof stickySrc === 'string' && stickySrc) {
          stickyImg.src = stickySrc.replace(/(\.[a-z0-9]+)(\?|$)/i, '_120x$1$2');
        } else if (stickyImg.dataset.defaultSrc) {
          // Variant has no image of its own: go back to the product shot
          // rather than leaving the previous variant's photo on screen.
          stickyImg.src = stickyImg.dataset.defaultSrc;
        }
      }

      /*
        The same scarcity, at the point of action. On mobile the buy box has
        usually scrolled away by the time this bar appears, so the count is
        repeated next to the only visible add-to-cart button - and hidden
        outright whenever the count is not known to be low.
      */
      var stickyLow = this.stickyBar.querySelector('[data-senzo-sticky-low]');
      if (stickyLow) {
        var stickyQty = this.lowStockCount(variant);
        var stickyLowText = stickyLow.querySelector('[data-senzo-sticky-low-text]');
        if (stickyLowText) {
          stickyLowText.textContent =
            stickyQty === null ? '' : fillQuantity(stickyLow.dataset.lowStockLabel, stickyQty, stickyLow.dataset.qtyToken);
        }
        stickyLow.hidden = stickyQty === null;
      }

      var stickySize = this.stickyBar.querySelector('[data-senzo-sticky-size]');
      if (stickySize && this.sizePicker) {
        var sizePos = parseInt(this.sizePicker.dataset.optionPosition, 10);
        stickySize.textContent = this.selectedOptions[sizePos] ? '· ' + this.selectedOptions[sizePos] : '';
      }
      var stickyAdd = this.stickyBar.querySelector('[data-senzo-sticky-add]');
      if (stickyAdd) {
        stickyAdd.disabled = !variant.available;
        stickyAdd.textContent = variant.available
          ? stickyAdd.dataset.idleLabel || stickyAdd.textContent
          : stickyAdd.dataset.soldOutLabel || stickyAdd.textContent;
      }
    }
  };

  /* -- Quantity ------------------------------------------------------------- */

  SenzoProduct.prototype.bindQuantity = function () {
    var self = this;
    this.root.querySelectorAll('[data-senzo-quantity]').forEach(function (control) {
      var input = control.querySelector('[data-senzo-qty-input]');
      var minus = control.querySelector('[data-senzo-qty-minus]');
      var plus = control.querySelector('[data-senzo-qty-plus]');
      if (!input) return;

      minus &&
        minus.addEventListener('click', function () {
          var value = Math.max(1, (parseInt(input.value, 10) || 1) - 1);
          input.value = value;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      plus &&
        plus.addEventListener('click', function () {
          var value = (parseInt(input.value, 10) || 1) + 1;
          input.value = value;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      input.addEventListener('change', function () {
        var value = parseInt(input.value, 10);
        if (!value || value < 1) input.value = 1;
      });
    });
  };

  /* -- Add to cart ------------------------------------------------------------ */

  SenzoProduct.prototype.bindAddToCart = function () {
    if (!this.form) return;
    var self = this;

    this.form.addEventListener('submit', function (event) {
      var submitter = event.submitter;
      // Let dynamic checkout / Shop Pay buttons submit natively.
      if (submitter && submitter.getAttribute('name') !== 'add') return;

      event.preventDefault();
      self.attemptAddToCart();
    });
  };

  SenzoProduct.prototype.attemptAddToCart = function () {
    // Validate a size is selected when the product has a size picker.
    if (this.sizePicker) {
      var position = parseInt(this.sizePicker.dataset.optionPosition, 10);
      if (!this.selectedOptions[position]) {
        var errorEl = this.root.querySelector('[data-senzo-size-error]');
        if (errorEl) {
          errorEl.hidden = false;
          errorEl.focus && errorEl.focus();
        }
        if (this.sizeGroup) {
          this.sizeGroup.scrollIntoView({ behavior: 'smooth', block: 'center' });
          var firstChip = this.sizeGroup.querySelector('[data-senzo-size]');
          firstChip && firstChip.focus();
        }
        return;
      }
    }

    if (this.currentVariant && this.currentVariant.available === false) return;

    this.submitAddToCart();
  };

  SenzoProduct.prototype.submitAddToCart = function () {
    var self = this;
    this.clearAddError(); // drop any message from a previous attempt
    var formData = new FormData(this.form);
    var button = this.addButton;
    var labelEl = button && button.querySelector('[data-senzo-add-label]');
    var spinnerEl = button && button.querySelector('[data-senzo-add-spinner]');

    if (button) {
      button.disabled = true;
      if (spinnerEl) spinnerEl.hidden = false;
      if (labelEl) labelEl.textContent = button.dataset.loadingLabel;
    }

    var cartItemsComponents = document.querySelectorAll('cart-items-component');
    var sectionIds = [];
    cartItemsComponents.forEach(function (el) {
      if (el.dataset && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });
    if (sectionIds.length) formData.set('sections', sectionIds.join(','));

    var variantId = formData.get('id');
    var quantity = Number(formData.get('quantity')) || 1;

    var deferred = null;
    var eventsReady = import('@shopify/events')
      .then(function (mod) {
        deferred = mod.CartLinesUpdateEvent.createPromise();
        document.dispatchEvent(
          new mod.CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity: quantity }],
            promise: deferred.promise,
          })
        );
        return mod;
      })
      .catch(function () {
        return null;
      });

    var cartAddUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';

    fetch(cartAddUrl, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok || result.data.status) {
          var message = (result.data && (result.data.description || result.data.message)) || 'Kon niet toevoegen';
          throw new Error(message);
        }

        var cartRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

        eventsReady.then(function (mod) {
          fetch(cartRoot + 'cart.js')
            .then(function (r) {
              return r.json();
            })
            .then(function (ajaxCart) {
              /*
                Always announce the new cart on our own event. The header count
                listens for this as well as Shopify's, so the UI stays correct
                even when @shopify/events is unavailable - which happens in any
                browser without import-map support. Previously every piece of
                post-add feedback hung off that module, so when it failed the
                item was added server-side but nothing on screen changed until
                a manual refresh.
              */
              document.dispatchEvent(
                new CustomEvent('senzo:cart:updated', { detail: { cart: ajaxCart } })
              );

              if (mod && deferred) {
                deferred.resolve({
                  cart: mod.CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                  detail: {
                    items: ajaxCart.items,
                    source: 'senzo-product-main',
                    sections: result.data.sections,
                    didError: false,
                  },
                });
              } else {
                // No Shopify events module: refresh and open the drawer ourselves.
                self.refreshCartSections(result.data.sections);
                self.openCartDrawer();
              }
            })
            .catch(function (err) {
              if (deferred) deferred.reject(err);
            });
        });

        self.onAddedSuccess(button, labelEl, spinnerEl);
      })
      .catch(function (error) {
        if (deferred) deferred.reject(error);
        // `error.message` carries Shopify's own description for a rejected add.
        self.onAddedError(button, labelEl, spinnerEl, error && error.message);
      });
  };

  SenzoProduct.prototype.onAddedSuccess = function (button, labelEl, spinnerEl) {
    if (spinnerEl) spinnerEl.hidden = true;
    if (button) {
      button.disabled = false;
      button.classList.add('is-added');
      if (labelEl) labelEl.textContent = button.dataset.addedLabel;
    }

    /*
      The drawer is deliberately NOT opened here. Horizon's own
      <cart-drawer-component> already opens itself on the cartLinesUpdate event
      we dispatch, and it sequences that correctly - it waits for the cart
      contents to re-render and defers when the add came from inside a modal.
      Opening it here as well raced that logic and could flash a stale drawer.

      Auto-opening is controlled by the "auto_open_cart_drawer" theme setting.
    */

    setTimeout(function () {
      if (button) {
        button.classList.remove('is-added');
        if (labelEl) labelEl.textContent = button.dataset.idleLabel;
      }
    }, 2500);
  };

  /*
    Re-renders the cart drawer from the section HTML that /cart/add.js returns.

    Normally Horizon's own <cart-items-component> refreshes itself when it hears
    the Shopify cartLinesUpdate event. If that module never loaded, nothing
    updates and the drawer keeps showing the cart as it was on page load - the
    item is in the cart server-side, but you only see it after a refresh.
  */
  SenzoProduct.prototype.refreshCartSections = function (sections) {
    if (!sections) return;

    Object.keys(sections).forEach(function (sectionId) {
      var host = document.getElementById('shopify-section-' + sectionId);
      if (!host || typeof sections[sectionId] !== 'string') return;

      var parsed = new DOMParser().parseFromString(sections[sectionId], 'text/html');
      var incoming = parsed.getElementById('shopify-section-' + sectionId);
      if (incoming) host.innerHTML = incoming.innerHTML;
    });
  };

  SenzoProduct.prototype.openCartDrawer = function () {
    if (this.cartType !== 'drawer') return;
    // Re-queried because refreshCartSections may have just replaced the node.
    var drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  };

  SenzoProduct.prototype.onAddedError = function (button, labelEl, spinnerEl, message) {
    if (spinnerEl) spinnerEl.hidden = true;
    if (button) {
      button.disabled = false;
      if (labelEl) labelEl.textContent = button.dataset.idleLabel;
    }

    /*
      Surface the reason. Shopify's /cart/add.js returns a human-readable
      `description` for real failures (out of stock, quantity limits), so we
      prefer that and fall back to the editable generic message.
    */
    var errorEl = this.root.querySelector('[data-senzo-add-error]');
    if (!errorEl) return;

    errorEl.textContent = message || errorEl.dataset.genericMessage || '';
    errorEl.hidden = !errorEl.textContent;

    clearTimeout(this._addErrorTimer);
    this._addErrorTimer = setTimeout(function () {
      errorEl.hidden = true;
    }, 8000);
  };

  SenzoProduct.prototype.clearAddError = function () {
    var errorEl = this.root.querySelector('[data-senzo-add-error]');
    if (errorEl) errorEl.hidden = true;
  };

  /* -- Notify (back-in-stock) ------------------------------------------------- */

  SenzoProduct.prototype.bindNotify = function () {
    // A sold-out collection card links to {product.url}#senzo-notify. Without
    // this the shopper lands on the product page with the panel still closed
    // and no idea why they were sent here.
    if (window.location.hash === '#senzo-notify') {
      var deepLinked = this.root.querySelector('[data-senzo-notify]');
      if (deepLinked) {
        /*
          Opening the panel is not enough. contact[body] is filled by
          toggleNotify from the chosen size, so a request arriving this way
          reached the merchant with no size in it at all, and this is the
          common path: every sold-out collection card links here. Select the
          first sold-out size so the request says which one it is for.
        */
        var soldOut = this.root.querySelector('[data-senzo-size][data-available="false"]');
        if (soldOut && typeof this.selectSizeChip === 'function') {
          this.selectSizeChip(soldOut);
        } else {
          deepLinked.hidden = false;
        }
        requestAnimationFrame(function () {
          deepLinked.scrollIntoView({ block: 'center' });
        });
      }
    }

    // Selection handled from selectSizeChip via toggleNotify; nothing to
    // bind here besides making sure the message template is ready.
  };

  SenzoProduct.prototype.toggleNotify = function (show, sizeValue) {
    var panel = this.root.querySelector('[data-senzo-notify]');
    if (!panel) return;
    panel.hidden = !show;
    if (!show) return;

    var template = this.sizePicker.dataset.notifyTemplate || '';
    var productTitle = document.title;
    var titleBlock = this.root.querySelector('.senzo-pdp__title');
    if (titleBlock) productTitle = titleBlock.textContent.trim();

    var message = template.replace('[product]', productTitle).replace('[size]', sizeValue);
    var bodyInput = panel.querySelector('[data-senzo-notify-body]');
    if (bodyInput) bodyInput.value = message;

    var klaviyoSlot = panel.querySelector('[data-senzo-klaviyo-slot]');
    if (klaviyoSlot) {
      var variant = this.findVariantByOptions(
        (function (self) {
          var opts = {};
          for (var k in self.selectedOptions) opts[k] = self.selectedOptions[k];
          return opts;
        })(this)
      );
      klaviyoSlot.dataset.variantId = variant ? variant.id : '';
    }
  };

  /* -- Size guide modal --------------------------------------------------------- */

  SenzoProduct.prototype.bindSizeGuide = function () {
    var opener = this.root.querySelector('[data-senzo-size-guide-open]');
    var dialog = this.root.querySelector('[data-senzo-size-guide]');
    if (!opener || !dialog) return;

    opener.addEventListener('click', function () {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    });

    var closeBtn = dialog.querySelector('[data-senzo-size-guide-close]');
    closeBtn &&
      closeBtn.addEventListener('click', function () {
        dialog.close ? dialog.close() : dialog.removeAttribute('open');
      });

    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) dialog.close && dialog.close();
    });
  };

  /* -- Share ---------------------------------------------------------------------- */

  SenzoProduct.prototype.bindShare = function () {
    var share = this.root.querySelector('[data-senzo-share]');
    if (!share) return;

    var nativeBtn = share.querySelector('[data-senzo-share-native]');
    var copyBtn = share.querySelector('[data-senzo-share-copy]');
    var title = share.dataset.shareTitle;
    var url = share.dataset.shareUrl;

    if (nativeBtn) {
      if (navigator.share) {
        nativeBtn.addEventListener('click', function () {
          navigator.share({ title: title, url: url }).catch(function () {});
        });
      } else {
        nativeBtn.hidden = true;
      }
    }

    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        var done = function () {
          var original = copyBtn.getAttribute('aria-label');
          copyBtn.classList.add('is-confirmed');
          copyBtn.setAttribute('aria-label', share.dataset.copiedLabel);
          setTimeout(function () {
            copyBtn.classList.remove('is-confirmed');
            copyBtn.setAttribute('aria-label', original);
          }, 2000);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(done).catch(function () {});
        } else {
          var temp = document.createElement('textarea');
          temp.value = url;
          document.body.appendChild(temp);
          temp.select();
          try {
            document.execCommand('copy');
            done();
          } catch (e) {
            /* no-op */
          }
          document.body.removeChild(temp);
        }
      });
    }
  };

  /* -- Description excerpt "read more" scroll ------------------------------------- */

  function findDetailsTarget(link) {
    // The details section is a separate section, so the buy box cannot build its
    // id. Look for the stable anchor first, then the href, then any details
    // section on the page.
    return (
      document.querySelector('[data-senzo-details-anchor]') ||
      (function () {
        var href = link && link.getAttribute('href');
        if (!href || href.charAt(0) !== '#' || href.length < 2) return null;
        try {
          return document.querySelector(href);
        } catch (e) {
          return null;
        }
      })() ||
      document.querySelector('.senzo-details-section')
    );
  }

  function revealDetails(target) {
    // Open the first panel so the customer actually lands on readable copy
    // instead of a row of closed headings.
    var panels =
      target.matches && target.matches('[data-senzo-panels]')
        ? target
        : target.querySelector('[data-senzo-panels]');
    if (panels && panels.senzoPanels) panels.senzoPanels.reveal(0);
  }

  SenzoProduct.prototype.bindExcerptScroll = function () {
    this.root.querySelectorAll('[data-senzo-scroll-to-details]').forEach(function (link) {
      var target = findDetailsTarget(link);

      // Nothing to scroll to (no details section on this template): drop the
      // link rather than leave a dead "Lees meer" on the page.
      if (!target) {
        link.hidden = true;
        link.setAttribute('aria-hidden', 'true');
        return;
      }

      if (!target.id) target.id = 'senzo-product-details';
      link.setAttribute('href', '#' + target.id);

      link.addEventListener('click', function (event) {
        event.preventDefault();
        revealDetails(target);
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (history.replaceState) history.replaceState(null, '', '#' + target.id);
      });
    });
  };

  /* -- Gallery ---------------------------------------------------------------------- */

  SenzoProduct.prototype.bindGallery = function () {
    var gallery = this.gallery;
    if (!gallery) return;
    var self = this;

    var track = gallery.querySelector('[data-senzo-gallery-track]');
    var slides = Array.prototype.slice.call(gallery.querySelectorAll('[data-senzo-media]'));
    var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-senzo-thumb]'));
    var dots = Array.prototype.slice.call(gallery.querySelectorAll('[data-senzo-dot]'));
    var prevBtn = gallery.querySelector('[data-senzo-gallery-prev]');
    var nextBtn = gallery.querySelector('[data-senzo-gallery-next]');

    this.gallerySlides = slides;
    this.galleryThumbs = thumbs;
    this.galleryDots = dots;
    this.galleryTrack = track;
    this.galleryIndex = 0;

    function activate(index, scroll) {
      if (index < 0 || index >= slides.length) return;
      self.galleryIndex = index;
      slides.forEach(function (slide, i) {
        slide.classList.toggle('is-active', i === index);
      });
      thumbs.forEach(function (thumb, i) {
        thumb.classList.toggle('is-active', i === index);
        thumb.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
      if (scroll !== false && track) {
        var target = slides[index];
        if (target && window.innerWidth < 750) {
          track.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
        }
      }
    }

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () {
        activate(i);
      });
    });
    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        activate(i);
      });
    });
    prevBtn &&
      prevBtn.addEventListener('click', function () {
        activate((self.galleryIndex - 1 + slides.length) % slides.length);
      });
    nextBtn &&
      nextBtn.addEventListener('click', function () {
        activate((self.galleryIndex + 1) % slides.length);
      });

    // Sync dots/thumbs while swiping on mobile.
    if (track && slides.length > 1) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
              var index = slides.indexOf(entry.target);
              if (index !== -1) activate(index, false);
            }
          });
        },
        { root: track, threshold: [0.6] }
      );
      slides.forEach(function (slide) {
        observer.observe(slide);
      });
    }

    this.activateGallerySlide = activate;
  };

  SenzoProduct.prototype.showGalleryMedia = function (mediaId) {
    if (!this.gallerySlides) return;
    var index = -1;
    this.gallerySlides.forEach(function (slide, i) {
      if (slide.dataset.mediaId === String(mediaId)) index = i;
    });
    if (index !== -1 && this.activateGallerySlide) this.activateGallerySlide(index);
  };

  /* -- Lightbox --------------------------------------------------------------------- */

  SenzoProduct.prototype.bindLightbox = function () {
    var dialog = this.root.querySelector('[data-senzo-lightbox]');
    if (!dialog) return;
    var self = this;
    var image = dialog.querySelector('[data-senzo-lightbox-image]');

    this.root.querySelectorAll('[data-senzo-zoom-trigger]').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var img = trigger.querySelector('[data-senzo-zoom-image]');
        if (img && image) {
          image.src = img.currentSrc || img.src;
          image.alt = img.alt;
        }
        var slide = trigger.closest('[data-senzo-media]');
        if (slide && self.gallerySlides) {
          self.lightboxIndex = self.gallerySlides.indexOf(slide);
        }
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      });
    });

    var closeBtn = dialog.querySelector('[data-senzo-lightbox-close]');
    closeBtn &&
      closeBtn.addEventListener('click', function () {
        dialog.close ? dialog.close() : dialog.removeAttribute('open');
      });
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) dialog.close && dialog.close();
    });

    function showAtIndex(index) {
      if (!self.gallerySlides || !self.gallerySlides.length) return;
      var len = self.gallerySlides.length;
      self.lightboxIndex = ((index % len) + len) % len;
      var slide = self.gallerySlides[self.lightboxIndex];
      var img = slide && slide.querySelector('[data-senzo-zoom-image]');
      if (img && image) {
        image.src = img.currentSrc || img.src;
        image.alt = img.alt;
      }
    }

    var prevBtn = dialog.querySelector('[data-senzo-lightbox-prev]');
    var nextBtn = dialog.querySelector('[data-senzo-lightbox-next]');
    prevBtn &&
      prevBtn.addEventListener('click', function () {
        showAtIndex((self.lightboxIndex || 0) - 1);
      });
    nextBtn &&
      nextBtn.addEventListener('click', function () {
        showAtIndex((self.lightboxIndex || 0) + 1);
      });

    dialog.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') showAtIndex((self.lightboxIndex || 0) - 1);
      if (event.key === 'ArrowRight') showAtIndex((self.lightboxIndex || 0) + 1);
    });
  };

  /* -- Sticky mobile bar -------------------------------------------------------------- */

  SenzoProduct.prototype.bindStickyBar = function () {
    if (!this.stickyBar) return;
    var self = this;
    var buyButtons = this.root.querySelector('.senzo-pdp__block--buy_buttons');
    if (!buyButtons) return;

    /*
      The bar is fixed to the bottom of the screen, so once the shopper has
      scrolled to the end of the page it sits on top of the last strip of the
      footer - the payment icons. It steps aside while the footer is on screen:
      by then the buy box is far away anyway, and the footer is what they came
      down to read.
    */
    var pastBuyButtons = false;
    var footerInView = false;
    var sync = function () {
      self.stickyBar.classList.toggle('is-visible', pastBuyButtons && !footerInView);
    };

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          pastBuyButtons = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          self.stickyBar.hidden = false;
        });
        sync();
      },
      { threshold: 0 }
    );
    observer.observe(buyButtons);

    var footer = document.querySelector('.senzo-footer') || document.querySelector('footer');
    if (footer) {
      new IntersectionObserver(
        function (entries) {
          footerInView = entries[0].isIntersecting;
          sync();
        },
        { threshold: 0 }
      ).observe(footer);
    }
  };

  /* ---------------------------------------------------------------------
   * PDP detail panels: one DOM, tabs on desktop, accordion on mobile
   *
   * The trigger buttons are moved between their accordion heading and the tab
   * strip instead of being rendered twice and hidden with CSS. Two copies
   * would mean two of every label in the accessibility tree, and CSS cannot
   * set `hidden`, which is the only thing that reliably keeps the inactive
   * copy out of it.
   *
   * 990px, not 750px: that is where the PDP becomes two columns, so it is the
   * only width at which this group is actually beside the photos. Below it,
   * three Dutch labels would wrap onto three lines - an accordion without an
   * accordion's affordances - so a real accordion is what mobile gets.
   * ------------------------------------------------------------------- */

  var PANELS_DESKTOP = '(min-width: 990px)';

  function bindDetailPanels(root) {
    var strip = root.querySelector('[data-senzo-panels-strip]');
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-senzo-panel]'));
    if (!strip || !items.length) return;

    var triggers = [];
    var bodies = [];
    var heads = [];
    var panels = [];

    items.forEach(function (item) {
      var trigger = item.querySelector('[data-senzo-panel-trigger]');
      var body = item.querySelector('[data-senzo-panel-body]');
      var head = item.querySelector('[data-senzo-panel-head]');
      if (!trigger || !body || !head) return;
      panels.push(item);
      triggers.push(trigger);
      bodies.push(body);
      heads.push(head);
    });
    if (!triggers.length) return;

    var desktopLayout = root.getAttribute('data-senzo-panels-layout') === 'accordion' ? 'accordion' : 'tabs';
    var label = strip.getAttribute('aria-label');
    var mq = window.matchMedia(PANELS_DESKTOP);
    var mode = '';

    // Liquid already rendered the collapsed/expanded state, so the script
    // starts from what is on screen rather than imposing its own and causing a
    // shift on load.
    var expanded = bodies.map(function (body) {
      return !body.hidden;
    });
    var active = expanded.indexOf(true);
    if (active === -1) active = 0;

    function applyAccordion() {
      strip.hidden = true;
      triggers.forEach(function (trigger, i) {
        if (trigger.parentNode !== heads[i]) heads[i].appendChild(trigger);
        heads[i].hidden = false;
        trigger.removeAttribute('role');
        trigger.removeAttribute('aria-selected');
        trigger.removeAttribute('tabindex');
        trigger.setAttribute('aria-expanded', expanded[i] ? 'true' : 'false');
        trigger.classList.toggle('is-active', expanded[i]);
        bodies[i].setAttribute('role', 'region');
        bodies[i].removeAttribute('tabindex');
        bodies[i].hidden = !expanded[i];
      });
    }

    function applyTabs() {
      strip.hidden = false;
      if (label) strip.setAttribute('aria-label', label);
      triggers.forEach(function (trigger, i) {
        // Appended in index order, so the strip always mirrors block order.
        if (trigger.parentNode !== strip) strip.appendChild(trigger);
        heads[i].hidden = true;
        trigger.setAttribute('role', 'tab');
        trigger.setAttribute('aria-selected', i === active ? 'true' : 'false');
        trigger.removeAttribute('aria-expanded');
        // Roving tabindex: one stop for the whole strip, arrows do the rest.
        trigger.tabIndex = i === active ? 0 : -1;
        trigger.classList.toggle('is-active', i === active);
        bodies[i].setAttribute('role', 'tabpanel');
        // A tabpanel holding only static text still needs to be focusable, or
        // keyboard users cannot reach content that may scroll.
        bodies[i].tabIndex = 0;
        bodies[i].hidden = i !== active;
      });
    }

    function apply() {
      if (mode === 'tabs') applyTabs();
      else applyAccordion();
    }

    function setMode(next) {
      if (mode === next) return;
      // Moving a button in the DOM drops focus, so put it back afterwards.
      var focused = triggers.indexOf(document.activeElement);
      mode = next;
      root.setAttribute('data-senzo-panels-mode', mode);
      if (mode === 'tabs') {
        var firstOpen = expanded.indexOf(true);
        if (firstOpen !== -1) active = firstOpen;
        // Exactly one panel is open in tab mode, always.
        expanded = triggers.map(function (_, i) {
          return i === active;
        });
      }
      apply();
      if (focused !== -1) triggers[focused].focus();
    }

    function selectTab(i, moveFocus) {
      active = i;
      expanded = triggers.map(function (_, k) {
        return k === i;
      });
      applyTabs();
      if (moveFocus) triggers[i].focus();
    }

    function toggleRow(i) {
      expanded[i] = !expanded[i];
      if (expanded[i]) active = i;
      applyAccordion();
    }

    triggers.forEach(function (trigger, i) {
      trigger.addEventListener('click', function () {
        if (mode === 'tabs') selectTab(i, false);
        else toggleRow(i);
      });

      trigger.addEventListener('keydown', function (event) {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        var last = triggers.length - 1;
        var next = -1;
        var tabs = mode === 'tabs';
        var prevKey = tabs ? 'ArrowLeft' : 'ArrowUp';
        var nextKey = tabs ? 'ArrowRight' : 'ArrowDown';

        if (event.key === nextKey) next = i === last ? 0 : i + 1;
        else if (event.key === prevKey) next = i === 0 ? last : i - 1;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = last;
        if (next === -1) return;

        event.preventDefault();
        // Tabs activate on arrow (APG's automatic activation - the panels are
        // already in the DOM, so there is nothing to wait for). Accordion
        // arrows only move focus; opening stays an explicit choice.
        if (tabs) selectTab(next, true);
        else triggers[next].focus();
      });
    });

    function sync() {
      setMode(desktopLayout === 'tabs' && mq.matches ? 'tabs' : 'accordion');
    }

    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', sync);
    else if (typeof mq.addListener === 'function') mq.addListener(sync);
    sync();

    // Used by the "Lees meer" link and by the theme editor's block selection.
    root.senzoPanels = {
      reveal: function (index) {
        var i = typeof index === 'number' && index >= 0 && index < triggers.length ? index : 0;
        if (mode === 'tabs') selectTab(i, false);
        else if (!expanded[i]) toggleRow(i);
      },
      indexOf: function (element) {
        for (var i = 0; i < panels.length; i++) {
          if (panels[i] === element || panels[i].contains(element)) return i;
        }
        return -1;
      }
    };
  }

  /* ---------------------------------------------------------------------
   * Related products: lazy-fetch via the Shopify Recommendations API
   * ------------------------------------------------------------------- */

  function bindRelated(root) {
    var url = root.dataset.url;
    if (!url) return;
    var grid = root.querySelector('[data-senzo-related-grid]');
    if (!grid) return;

    // Hide the whole section - heading included - not just the grid. Hiding the
    // grid alone left the heading floating above the section's own padding.
    var section = root.closest('[data-senzo-related-section]') || root;

    var done = false;

    function load() {
      if (done) return;
      done = true;

      fetch(url)
        .then(function (response) {
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var freshGrid = doc.querySelector('[data-senzo-related-grid]');
          var hasCards = freshGrid && freshGrid.querySelector('.senzo-related__item');
          if (hasCards) {
            grid.innerHTML = freshGrid.innerHTML;
          } else {
            section.hidden = true;
          }
        })
        .catch(function () {
          section.hidden = true;
        });
    }

    if (typeof IntersectionObserver === 'function') {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            observer.disconnect();
            load();
          });
        },
        { rootMargin: '400px 0px' }
      );
      observer.observe(root);
    }

    // Safety net: the skeletons must never be the final state of the page. If the
    // observer has not fired shortly after load (it never does in some embedded
    // and headless browsers) fetch anyway.
    setTimeout(load, 2000);
  }

  /* ---------------------------------------------------------------------
   * Init
   * ------------------------------------------------------------------- */

  function initAll(scope) {
    scope = scope || document;
    scope.querySelectorAll('[data-senzo-product]:not([data-senzo-initialized])').forEach(function (root) {
      root.setAttribute('data-senzo-initialized', 'true');
      new SenzoProduct(root);
    });
    scope.querySelectorAll('[data-senzo-panels]:not([data-senzo-initialized])').forEach(function (root) {
      root.setAttribute('data-senzo-initialized', 'true');
      bindDetailPanels(root);
    });
    scope.querySelectorAll('[data-senzo-related]:not([data-senzo-initialized])').forEach(function (root) {
      root.setAttribute('data-senzo-initialized', 'true');
      bindRelated(root);
    });
  }

  initAll();

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  // Theme editor: clicking a panel block in the sidebar has to open that panel,
  // otherwise the client edits copy they cannot see.
  document.addEventListener('shopify:block:select', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var panel = target.closest('[data-senzo-panel]');
    if (!panel) return;
    var root = panel.closest('[data-senzo-panels]');
    if (!root || !root.senzoPanels) return;
    root.senzoPanels.reveal(root.senzoPanels.indexOf(panel));
  });
})();
